import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { selectResourceId } from "../../app/auth/createAuthSlice";
import { BsBookmark, BsBookmarkFill } from "react-icons/bs";
import { IHomeWhatsNewItem, IPosts } from "../../app/models/HomeModal";
import { useBookmarkWhatsNewMutation } from "../../app/appApiSlice";
import { MdOutlineLocationOn } from "react-icons/md"; 
import { toSentenceCase } from "../../shared/commonFunctions";
import { RiVideoOnLine } from "react-icons/ri";
import { showAppToast } from "../../components/AppToast";

type ActionableCardItemProps = {
  Posts: IHomeWhatsNewItem | IPosts;
  onBookmarkChange?: (cardId: number, isBookmarked: boolean) => void;
};

const ActionableCardItem = ({ Posts, onBookmarkChange } : ActionableCardItemProps) => {
  const post = ((Posts as IHomeWhatsNewItem)?.Posts ?? Posts) as IPosts;
  const resourceId = useAppSelector(selectResourceId);
  const [bookmarked, setBookmarked] = useState(post?.isBookmarked);
  const [isExpanded, setIsExpanded] = useState(false);

  const navigate = useNavigate();

 const [ bookmarkWhatsNew, { isLoading } ] = useBookmarkWhatsNewMutation()

 useEffect(() => {
   setBookmarked(post?.isBookmarked);
 }, [post?.id, post?.isBookmarked]);

 const toggleWhatsNewBookmark = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (isLoading) return;
    const nextBookmarked = !bookmarked;

    bookmarkWhatsNew({ cardId: post?.id, userId: resourceId })
      .unwrap()
      .then((res) => { 
        if (res === true) {
          setBookmarked(nextBookmarked);
          onBookmarkChange?.(post?.id, nextBookmarked);
          !bookmarked && showAppToast({
            message: (
              <span>
                {post?.contentType} saved.{' '}
                <a
                  href="#"
                  className="underline"
                    onClick={(e) => {
                      e.preventDefault();
                      toast.dismiss(); 
                      navigate(`/insights`, {
                        state: { showTab: "Saved" },
                      });
                    }}
                >
                  Click here to view
                </a>
              </span>
            ),
            color: "blue",
          });
          bookmarked && showAppToast({
            message: `${post?.contentType} Unsaved.`,
            color: "blue",
          });
        }
      })
      .catch((error) => {
        console.error('Failed to bookmark:', error);         
      });
  };

  const toggleDescription = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };
  
  const description = post?.description || '';
  const shouldTruncate = description?.length > 100;
  const shouldShowBookmark =
    post?.contentType !== "In-person event" && post?.contentType !== "Virtual Event";


  return (
    <div className="border border-lightGreyBorder flex flex-col">
      <div className="flex flex-col talent-info flex-1">
        <div className='relative'>
          <div
            className="overflow-hidden transition-[max-height] duration-500 ease-in-out"
            style={{ maxHeight: isExpanded ? "70px" : "9rem" }}
          >
            <img src={post?.imageUrl} alt="" className='w-full'/>
          </div>
          <span className='bg-bgBlue text-white text-xs p-1 absolute top-2 left-2'> {post?.contentType} </span>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <div className="flex justify-between gap-2">
            <h2 className="text-base flex-1 font-semibold sd:text-sm">{post?.title}</h2>
            {shouldShowBookmark && (
              <a
                href=""
                className={`text-xl mt-2 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
                onClick={toggleWhatsNewBookmark}
                aria-busy={isLoading}
              >
                {
                  bookmarked ? (
                    <BsBookmarkFill className="text-primary" />
                  ) : (
                    <BsBookmark className="text-primary"/>
                  )}
              </a>
            )}
          </div>
          <div className="text-xs text-grey1 flex items-center gap-3 mb-3 mt-1">
            {
              post?.location ? 
              <>
              <span className="flex gap-[0.8px] items-center">
                {post?.contentType === "In-person event" && <MdOutlineLocationOn size={15}/>}
                {post?.contentType === "Virtual Event" && <RiVideoOnLine size={13} />}
                <span>{post?.location ? ` ${post?.location}` : ''}</span>
              </span> | <span>{post?.displayDate}</span> 
              </> :
            <span>{post?.displayDate}</span>
            }
          </div>
          <p className={`text-xs text-[rgba(16,24,40,.6)] leading-normal ${isExpanded ? '' : 'line-clamp-2'}`}>
            {description}
          </p>
          {shouldTruncate && (
            <a
              href=''
              className='text-bgBlue cursor-pointer text-xs'
              onClick={toggleDescription}
            >
              {isExpanded ? 'Read less' : 'Read more'}
            </a>
          )}
        </div>
      </div>
      {post?.linkUrl && (
        <div className="flex gap-2 p-4 pt-0 w-full">
          <a className="text-primary underline text-sm" href={post?.linkUrl} target="_blank" rel="noopener noreferrer">
            {toSentenceCase(post?.linkText)}
          </a>
        </div>
      )}
    </div>
  );
};

export default ActionableCardItem;
